// src/modules/lodrive/Lodrive.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from 'react';
import './Lodrive.css';
import {
  LuFolder, LuFolderPlus, LuUpload, LuFile, LuFileText, LuImage, LuVideo,
  LuMoreVertical, LuArrowLeft, LuHome, LuMove, LuX, LuCheck,
  LuPanelRightOpen, LuPanelRightClose, LuMenu, LuChevronRight, LuChevronDown, LuArchive
} from 'react-icons/lu';
import Services from '../../services/Services';
import DragAndDropFolderAndFileUpload from '../../components/DragAndDropFileUpload/DragAndDropFolderAndFileUpload';
import { useLanguage } from '../../LanguageProvider';
import { GeneralContext } from '../../GeneralProvider';

const ROOT_ID = 'root';
const DND_INTERNAL = 'application/x-lodrive-item-ids';
const isFolder = (it) => it?.type === 'folder';

// Sanal ROOT
const VROOT = { id: ROOT_ID, type: 'folder', name: 'Lodrive', parentId: null, createdAt: 0 };
const getItem = (items, id) => (id === ROOT_ID ? VROOT : items[id]);

const fileIconByName = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return <LuImage />;
  if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) return <LuVideo />;
  if (['txt', 'md', 'json', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'].includes(ext)) return <LuFileText />;
  return <LuFile />;
};

function formatBytes(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let val = b / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${units[i]}`;
}

const buildPath = (items, folderId) => {
  const path = [];
  let curId = folderId;
  while (true) {
    const cur = getItem(items, curId);
    if (!cur) break;
    path.unshift(cur);
    if (!cur.parentId) break;
    curId = cur.parentId;
  }
  return path;
};

const getDescendants = (items, folderId) => {
  const out = [];
  const stack = [folderId];
  while (stack.length) {
    const f = stack.pop();
    for (const it of Object.values(items)) {
      if (it.parentId === f) {
        out.push(it.id);
        if (isFolder(it)) stack.push(it.id);
      }
    }
  }
  return out;
};

// ---------- Utilities ----------
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read error'));
    reader.onload = () => {
      const res = reader.result || '';
      const b64 = typeof res === 'string' ? res.split(',').pop() : '';
      resolve(b64 || '');
    };
    reader.readAsDataURL(file);
  });

/**
 * Aynı parent+name için paralel create'leri kilitlemek ve dup klasörü önlemek için
 * component-ömürlük lock haritası.
 */
const folderCreateLockRefGlobal = new Map();

/**
 * parentRemoteId altında 'name' adlı klasörü bulur; yoksa oluşturur.
 */
async function getOrCreateChildFolderRemoteId(token, parentRemoteId, name) {
  const lockKey = `${parentRemoteId || 'ROOT'}::${name}`;
  if (folderCreateLockRefGlobal.has(lockKey)) {
    return folderCreateLockRefGlobal.get(lockKey);
  }

  const p = (async () => {
    let childrenResp;
    if (parentRemoteId) {
      childrenResp = await Services.LodriveGetDirectoryContent(token, parentRemoteId);
    } else {
      childrenResp = await Services.LodriveGetRootDirectoryContents(token);
    }
    const result = childrenResp?.data?.result || {};
    const dirs = Array.isArray(result.directories) ? result.directories : [];
    const found = dirs.find((d) => d.title === name);
    if (found?.directory_id) return found.directory_id;

    const createResp = await Services.LodriveCreateFolder(
      token,
      name,
      parentRemoteId ?? undefined
    );
    const newId = createResp?.data?.result;
    if (!newId) throw new Error('Directory create failed (no directoryID)');
    return newId;
  })();

  folderCreateLockRefGlobal.set(lockKey, p);
  try {
    const id = await p;
    return id;
  } finally {
    folderCreateLockRefGlobal.delete(lockKey);
  }
}

/**
 * a/b/c gibi bir path'i sırayla oluşturur (yalnızca uzakta).
 */
async function ensureRemoteFolderPathRemoteOnly(token, startRemoteId, dirNames, cacheMap) {
  let remoteParent = startRemoteId || null;

  for (const name of dirNames) {
    const key = `${remoteParent || 'ROOT'}::${name}`;
    if (cacheMap.has(key)) {
      remoteParent = cacheMap.get(key);
      continue;
    }
    const childId = await getOrCreateChildFolderRemoteId(token, remoteParent, name);
    cacheMap.set(key, childId);
    remoteParent = childId;
  }

  return remoteParent; // lastRemoteId
}

async function readDataTransferAsFiles(dt) {
  const out = [];

  const items = Array.from(dt.items || []);
  if (items.length) {
    const walkEntry = async (entry, parentPath = '') => {
      if (!entry) return;
      if (entry.isFile) {
        await new Promise(resolve => {
          entry.file(file => {
            const path = parentPath ? `${parentPath}/${file.name}` : file.name;
            out.push({ file, path });
            resolve();
          }, resolve);
        });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        await new Promise(async (resolve) => {
          const readAll = async () => {
            reader.readEntries(async (entries) => {
              if (entries && entries.length) {
                for (const e of entries) {
                  await walkEntry(e, parentPath ? `${parentPath}/${entry.name}` : entry.name);
                }
                await readAll();
              } else {
                resolve();
              }
            }, resolve);
          };
          await readAll();
        });
      }
    };

    for (const it of items) {
      const entry = it.getAsEntry?.() || it.webkitGetAsEntry?.() || null;
      if (entry) {
        await walkEntry(entry, '');
      } else {
        const f = it.getAsFile?.();
        if (f) {
          const path = f.webkitRelativePath || f.name;
          out.push({ file: f, path });
        }
      }
    }
  }

  if (out.length === 0) {
    const files = Array.from(dt.files || []);
    for (const f of files) {
      const path = f.webkitRelativePath || f.name;
      out.push({ file: f, path });
    }
  }

  return out;
}

export default function Lodrive() {

  const { subscriptionType } = useContext(GeneralContext);
  const { getTranslation } = useLanguage();
  const token = localStorage.getItem('token');
  const [items, setItems] = useState({});
  const [currentFolderId, setCurrentFolderId] = useState(ROOT_ID);
  const [selection, setSelection] = useState([]);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renamingSource, setRenamingSource] = useState(null);
  const [movingIds, setMovingIds] = useState(null);
  const [hoveringFolder, setHoveringFolder] = useState(null);
  const [showUploader, setShowUploader] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadsBusy, setUploadsBusy] = useState(false);

  const [pendingDelete, setPendingDelete] = useState(new Set()); // id set
  const [pendingMove, setPendingMove] = useState(new Set());     // id set

  const uploaderRef = useRef(null);

  const [driveSize, setDriveSize] = useState({
    used: 0,
    total: 0,
    percent: 0,
    loading: false,
    error: null,
    fetchedAt: null,
  });

  // ---- CACHE: Hangi klasörlerin içeriği alındı? ----
  const fetchedRef = useRef(new Set()); // folderId set
  const hasFetched = useCallback((id) => fetchedRef.current.has(id), []);
  const markFetched = useCallback((id) => { if (id) fetchedRef.current.add(id); }, []);

  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const currentFolder = getItem(items, currentFolderId);
  const path = useMemo(() => buildPath(items, currentFolderId), [items, currentFolderId]);

  const fetchDriveSize = useCallback(async () => {
    if (!token) return;
    setDriveSize(s => ({ ...s, loading: true, error: null }));
    try {
      const resp = await Services.LodriveGetSizeOfMyDrive(token);
      const r = resp?.data?.result ?? {};
      const sizeRaw = Number(r.size) || 0;
      const unit = String(r.unit || 'byte').toLowerCase();

      const usedBytes =
        unit.startsWith('byte') ? sizeRaw :
          unit === 'kb' ? sizeRaw * 1024 :
            unit === 'mb' ? sizeRaw * 1024 ** 2 :
              unit === 'gb' ? sizeRaw * 1024 ** 3 :
                sizeRaw;

      setDriveSize({
        used: usedBytes,
        total: 0,
        percent: 0,
        loading: false,
        error: null,
        fetchedAt: Date.now(),
      });
    } catch (e) {
      console.error('LodriveGetSizeOfMyDrive error:', e);
      setDriveSize(s => ({ ...s, loading: false, error: e?.message || 'fetch error' }));
    }
  }, [token]);

  useEffect(() => { fetchDriveSize(); }, [fetchDriveSize]);

  const children = useMemo(() => {
    const list = Object.values(items).filter(it => it.parentId === currentFolderId);
    return list.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name, 'tr');
    });
  }, [items, currentFolderId]);

  // ----- İlk açılış: ROOT'u zorunlu fetch et -----
  useEffect(() => {
    (async () => {
      await refreshVisibleTree(ROOT_ID, { forceRefetch: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }, []);

  // ---------- Service -> Items adaptörleri ----------
  const ingestRootContents = useCallback((payload) => {
    const next = { ...itemsRef.current };
    for (const it of Object.values(next)) {
      if (it.parentId === ROOT_ID) delete next[it.id];
    }

    const dirs = Array.isArray(payload?.directories) ? payload.directories : [];
    const files = Array.isArray(payload?.files) ? payload.files : [];

    for (const d of dirs) {
      const id = d.directory_id;
      next[id] = {
        id,
        type: 'folder',
        name: d.title,
        parentId: ROOT_ID,
        createdAt: Number(d.created_date_time) * 1000 || Date.now(),
        remoteId: id,
      };
    }

    for (const f of files) {
      const id = f.file_id;
      next[id] = {
        id,
        type: 'file',
        name: f.file_original_name,
        parentId: ROOT_ID,
        createdAt: Number(f.created_date_time) * 1000 || Date.now(),
        remoteFileId: f.file_id,
        mime: f.file_extension || 'application/octet-stream',
        size: Number(f.file_size) || 0,
        remoteKey: f.s3_key,
      };
    }

    setItems(next);
    markFetched(ROOT_ID);
  }, [markFetched]);

  const ingestDirectoryContents = useCallback((parentRemoteId, payload) => {
    const next = { ...itemsRef.current };

    for (const it of Object.values(next)) {
      if (it.parentId === parentRemoteId) delete next[it.id];
    }

    if (!next[parentRemoteId]) {
      next[parentRemoteId] = {
        id: parentRemoteId,
        type: 'folder',
        name: '(folder)',
        parentId: ROOT_ID,
        createdAt: Date.now(),
        remoteId: parentRemoteId,
      };
    }

    const dirs = Array.isArray(payload?.directories) ? payload.directories : [];
    for (const d of dirs) {
      const id = d.directory_id;
      next[id] = {
        id,
        type: 'folder',
        name: d.title,
        parentId: parentRemoteId,
        createdAt: Number(d.created_date_time) * 1000 || Date.now(),
        remoteId: id,
      };
    }

    const files = Array.isArray(payload?.files) ? payload.files : [];
    for (const f of files) {
      const id = f.file_id;
      next[id] = {
        id,
        type: 'file',
        name: f.file_original_name,
        parentId: parentRemoteId,
        createdAt: Number(f.created_date_time) * 1000 || Date.now(),
        remoteFileId: f.file_id,
        mime: f.file_extension || 'application/octet-stream',
        size: Number(f.file_size) || 0,
        remoteKey: f.s3_key,
      };
    }

    setItems(next);
    markFetched(parentRemoteId);
  }, [markFetched]);

  const refetchFolderOnly = useCallback(async (folderId) => {
    try {
      if (!folderId || folderId === ROOT_ID) {
        const rootResp = await Services.LodriveGetRootDirectoryContents(token);
        ingestRootContents(rootResp?.data?.result || {});
      } else {
        const resp = await Services.LodriveGetDirectoryContent(token, folderId);
        ingestDirectoryContents(folderId, resp?.data?.result || {});
      }
    } catch (e) {
      console.error('refetchFolderOnly error:', e);
    }
  }, [token, ingestRootContents, ingestDirectoryContents]);
  /**
   * Ağır işi yapan loader.
   * - ROOT fetch: sadece forceRefetch ya da daha önce alınmadıysa.
   * - Klasör fetch: sadece forceRefetch ya da daha önce alınmadıysa.
   * - Atalar: sadece daha önce alınmadıysa (patikayı doldurmak için).
   */
  const refreshVisibleTree = useCallback(
    async (preferFolderId, opts = {}) => {
      const { forceRefetch = false } = opts;
      setIsLoading(true);
      try {
        // ROOT'u duruma göre getir
        if (forceRefetch || !hasFetched(ROOT_ID)) {
          const rootResp = await Services.LodriveGetRootDirectoryContents(token);
          ingestRootContents(rootResp?.data?.result || {});
        }

        // İstenen klasör ROOT değilse ve gerekliyse getir
        if (preferFolderId && preferFolderId !== ROOT_ID) {
          if (forceRefetch || !hasFetched(preferFolderId)) {
            const curResp = await Services.LodriveGetDirectoryContent(token, preferFolderId);
            ingestDirectoryContents(preferFolderId, curResp?.data?.result || {});
          }
        }

        // Patikadaki ataları (daha önce alınmadıysa) doldur
        const maybePath = buildPath(itemsRef.current, preferFolderId)
          .map(x => x.id)
          .filter(id => id !== ROOT_ID && id !== preferFolderId);

        for (const ancestorId of maybePath) {
          if (!hasFetched(ancestorId)) {
            try {
              const aResp = await Services.LodriveGetDirectoryContent(token, ancestorId);
              ingestDirectoryContents(ancestorId, aResp?.data?.result || {});
            } catch { /* yoksay */ }
          }
        }

        setCurrentFolderId(preferFolderId);
      } catch (e) {
        console.error('refreshVisibleTree error:', e);
      } finally {
        setIsLoading(false);
      }
    },
    [token, ingestRootContents, ingestDirectoryContents, hasFetched]
  );

  // ---------- MOVE HELPERS ----------
  const moveItemsToTarget = useCallback(async (targetFolderId, ids) => {
    if (!ids?.length) return;
    // Forbidden: moving into itself / descendants handled by UI; double check here.
    const forbidden = new Set();
    (ids || []).forEach(id => {
      const it = itemsRef.current[id];
      if (it?.type === 'folder') getDescendants(itemsRef.current, id).forEach(d => forbidden.add(d));
      forbidden.add(id);
    });
    if (forbidden.has(targetFolderId)) return;

    const sourceParents = new Set(
      ids.map(id => (itemsRef.current[id]?.parentId ?? ROOT_ID))
    );

    // Visual busy
    setPendingMove(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });

    try {
      const targetRemoteId = targetFolderId === ROOT_ID ? '' : (itemsRef.current[targetFolderId]?.remoteId ?? targetFolderId);

      // Sequential to keep it simple/robust
      for (const id of ids) {
        const it = itemsRef.current[id];
        if (!it) continue;
        try {
          if (it.type === 'file') {
            const fileID = it.remoteFileId;
            if (!fileID) continue;
            await Services.LodriveMoveFileToDirectory(token, targetRemoteId, fileID);
          } else if (it.type === 'folder') {
            const directoryID = it.remoteId ?? it.id;
            await Services.LodriveMoveDirectoryToDirectory(token, targetRemoteId, directoryID);
          }
        } catch (err) {
          console.error('Move single item failed:', it?.name, err);
        }
      }

      const toRefetch = new Set([...sourceParents, targetFolderId]);
      await Promise.all([...toRefetch].map(refetchFolderOnly));
    } catch (e) {
      console.error('moveItemsToTarget error:', e);
    } finally {
      await refreshVisibleTree(currentFolderId, { forceRefetch: true });
      setPendingMove(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setSelection([]);
    }
  }, [token, currentFolderId, refreshVisibleTree]);

  const downloadDirectoryAsZip = useCallback(async (folderId) => {
    try {
      const remoteId = folderId === ROOT_ID ? null : (itemsRef.current[folderId]?.remoteId ?? folderId);
      if (!remoteId) return; // root için zip endpoint yoksa atla
      const resp = await Services.LodriveDownloadDirectory(token, remoteId);
      const url = resp?.data?.result?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('downloadDirectoryAsZip error:', e);
    }
  }, [token]);

  // ---------- UI Actions ----------
  const openFolder = useCallback(async (id, { forceRefetch = false } = {}) => {
    // Eğer cache varsa ve force yoksa: servis çağırmadan sadece geçiş
    if (!forceRefetch && hasFetched(id)) {
      setCurrentFolderId(id);
      setSelection([]);
      return;
    }
    setIsLoading(true);
    try {
      await refreshVisibleTree(id, { forceRefetch });
    } finally {
      setIsLoading(false);
    }
    setSelection([]);
  }, [hasFetched, refreshVisibleTree]);

  const quickOpen = useCallback((id) => {
    // Tamamen local geçiş (sidebar için)
    setCurrentFolderId(id);
    setSelection([]);
  }, []);

  const createFolder = useCallback(async () => {
    if (isLoading) return;
    try {
      const parentRemoteId =
        currentFolderId === ROOT_ID
          ? undefined
          : (items[currentFolderId]?.remoteId ?? currentFolderId);

      const desiredName = 'New Folder';
      const uniqueName = getNextAvailableFolderName(itemsRef.current, currentFolderId, desiredName);

      const resp = await Services.LodriveCreateFolder(token, uniqueName, parentRemoteId);
      const remoteId = resp?.data?.result;

      if (!remoteId) return;

      // Oluşturduğumuz klasörün bulunduğu klasörü tazele (fetch)
      await refreshVisibleTree(currentFolderId, { forceRefetch: true });

      // Ekranda rename için seç
      let tries = 0;
      while (tries < 2) {
        const list = Object.values(itemsRef.current)
          .filter(x => x.parentId === currentFolderId && x.id === remoteId);
        if (list.length) break;
        await new Promise(r => setTimeout(r, 250));
        await refreshVisibleTree(currentFolderId, { forceRefetch: false });
        tries++;
      }

      setRenamingId(remoteId);
      setRenamingSource('grid');
    } catch (err) {
      console.error('CreateFolder error:', err);
    }
  }, [token, currentFolderId, items, isLoading, refreshVisibleTree]);

  const commitRename = async (id, newName) => {
    if (id === ROOT_ID) { setRenamingId(null); setRenamingSource(null); return; }
    const item = items[id];
    const trimmed = (newName || '').trim();
    setRenamingId(null);
    setRenamingSource(null);
    if (!trimmed || !item) return;

    try {
      if (item.type === 'folder') {
        const uniqueName = getNextAvailableFolderName(
          { ...items, [id]: { ...items[id], name: '(temp-placeholder)' } },
          item.parentId ?? ROOT_ID,
          trimmed
        );
        await Services.LodriveUpdateFolderTitle(token, item.remoteId, uniqueName);
        setItems(prev => ({ ...prev, [id]: { ...prev[id], name: uniqueName } }));
        await refreshVisibleTree(currentFolderId, { forceRefetch: false });
      } else if (item.type === 'file') {
        // DOSYA: Servis ile rename et
        const fileID = item.remoteFileId;
        if (!fileID) return;
        await Services.LodriveRenameFile(token, fileID, trimmed);
        setItems(prev => ({ ...prev, [id]: { ...prev[id], name: trimmed } }));
        await refreshVisibleTree(currentFolderId, { forceRefetch: false });
      }
    } catch (e) {
      console.error('Rename error:', e);
    }
  };

  const removeMany = async (ids) => {
    if (uploadsBusy) return;
    const toRemove = new Set();
    for (const id of ids) {
      if (id === ROOT_ID) continue;
      const it = items[id];
      if (!it) continue;
      toRemove.add(id);
      if (isFolder(it)) getDescendants(items, id).forEach(x => toRemove.add(x));
    }
    if (!toRemove.size) return;

    setPendingDelete(prev => {
      const next = new Set(prev);
      toRemove.forEach(id => next.add(id));
      return next;
    });

    try {
      for (const rid of toRemove) {
        const it = items[rid];
        if (!it) continue;
        if (it.type === 'folder' && it.remoteId) {
          await Services.LodriveRemoveFolder(token, it.remoteId);
          // Silinen klasörü cache'ten çıkar
          fetchedRef.current.delete(rid);
        } else if (it.type === 'file' && it.remoteFileId) {
          await Services.LodriveRemoveFile(token, it.remoteFileId);
        }
      }
    } catch (e) {
      console.error('Remove error:', e);
    } finally {
      const newFolderId = toRemove.has(currentFolderId) ? ROOT_ID : currentFolderId;
      await refreshVisibleTree(newFolderId, { forceRefetch: true });
      fetchDriveSize();
      setSelection([]);
      setPendingDelete(prev => {
        const next = new Set(prev);
        toRemove.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const onOpenContext = (e, id, source = 'grid') => {
    e.preventDefault();
    setSelection(prev => (prev.includes(id) ? prev : [id]));
    const PADDING = 8;
    const WIDTH = 240;
    const HEIGHT = 196;
    const x = Math.max(PADDING, Math.min(e.clientX, window.innerWidth - WIDTH - PADDING));
    const y = Math.max(PADDING, Math.min(e.clientY, window.innerHeight - HEIGHT - PADDING));
    setCtxMenu({ x, y, id, source });
  };
  const closeContext = () => setCtxMenu(null);

  const uploadSingleFileWithHierarchy = useCallback(
    async ({ file, path, targetFolderId = null }) => {
      try {
        const rawPath = (path || file?.webkitRelativePath || file?.name || '').toString().replace(/\\/g, '/');
        const parts = rawPath.split('/').filter(Boolean);
        const fileName = parts.length > 1 ? parts[parts.length - 1] : (file?.name || 'file');
        const dirNames = parts.length > 1 ? parts.slice(0, -1) : [];

        const pathCache = new Map();

        // 🔒 KRİTİK: targetFolderId geldiyse onu KULLAN (snapshot).
        // Aksi halde (teorik) currentFolderId’e düş.
        const startRemoteId =
          targetFolderId != null
            ? (targetFolderId === ROOT_ID ? null : (itemsRef.current[targetFolderId]?.remoteId ?? targetFolderId))
            : (currentFolderId === ROOT_ID ? null : (itemsRef.current[currentFolderId]?.remoteId ?? currentFolderId));

        let parentRemoteId = startRemoteId;
        if (dirNames.length) {
          parentRemoteId = await ensureRemoteFolderPathRemoteOnly(
            token,
            startRemoteId,
            dirNames,
            pathCache
          );
        }

        const base64 = await fileToBase64(file);
        const dot = fileName.lastIndexOf('.');
        const ext = dot > 0 ? fileName.slice(dot + 1) : '';
        const filesPayload = [{ fileName, extension: ext, file: base64 }];

        const result = await Services.LodriveUploadFile(token, parentRemoteId, filesPayload);
        const ok = !!result?.data;
        return ok;
      } catch (err) {
        console.error('uploadSingleFileWithHierarchy error:', err);
        return false;
      }
    },
    [token, currentFolderId]
  );

  const downloadFile = useCallback(async (fileItem) => {
    try {
      const resp = await Services.LodriveDownloadFile(token, fileItem.remoteFileId);
      const url = resp?.data?.result?.url;
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Download error:', e);
    }
  }, [token]);

  useEffect(() => {
    const onKey = (e) => {
      if (!selection.length) return;
      if (e.key === 'Delete') {
        if (uploadsBusy) return;
        removeMany(selection);
      }
      if (e.key === 'F2' && selection.length === 1) {
        const id = selection[0];
        if (id === ROOT_ID) return;
        const it = items[id];
        if (!it) return;
        // klasör/dosya ayrımı yapmadan rename moda al
        setRenamingId(id);
        setRenamingSource('grid');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selection, items, uploadsBusy]);

  const onContentDragEnter = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
  }, []);
  const onContentDragOver = useCallback((e) => {
    if (e.dataTransfer) {
      // İç move / dosya yükleme ayrımı
      const hasInternal = !!e.dataTransfer.types?.includes(DND_INTERNAL);
      e.dataTransfer.dropEffect = hasInternal ? 'move' : 'copy';
    }
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const onContentDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
  }, []);

  const onContentDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // İç hareket mi?
    const internal = e.dataTransfer?.getData?.(DND_INTERNAL);
    if (internal) {
      try {
        const ids = JSON.parse(internal);
        // İçeriğe drop → mevcut klasöre taşı
        await moveItemsToTarget(currentFolderId, ids);
      } catch (err) {
        console.error('internal drop parse error', err);
      }
      return;
    }

    // Dış dosya yükleme
    await uploaderRef.current?.enqueueFromDataTransfer?.(e.dataTransfer, null);
    setShowUploader(true);
  }, [currentFolderId, moveItemsToTarget]);

  function getNextAvailableFolderName(items, parentId, desiredName) {
    const siblingNames = new Set(
      Object.values(items)
        .filter(it => it.type === 'folder' && (it.parentId ?? ROOT_ID) === (parentId ?? ROOT_ID))
        .map(it => it.name.toLowerCase())
    );
    const raw = (desiredName || 'New Folder').trim();
    const m = raw.match(/^(.*)\s\((\d+)\)$/);
    const base = (m ? m[1] : raw).trim();
    if (!siblingNames.has(base.toLowerCase())) return base;
    let n = 1;
    while (siblingNames.has(`${base} (${n})`.toLowerCase())) n++;
    return `${base} (${n})`;
  }

  const onBreadcrumbDragOver = (e, crumbId) => {
    if (!e.dataTransfer?.types?.includes(DND_INTERNAL)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onBreadcrumbDrop = async (e, crumbId) => {
    const internal = e.dataTransfer?.getData?.(DND_INTERNAL);
    if (!internal) return;
    e.preventDefault();
    try {
      const ids = JSON.parse(internal);
      await moveItemsToTarget(crumbId, ids);
    } catch (err) {
      console.error('breadcrumb drop parse error', err);
    }
  };

  return (
    <div className="lodrive-root" style={{ cursor: 'var(--cursor)' }}>
      {/* Toolbar */}
      <div className="lodrive-toolbar">
        <div className="lodrive-toolbar-left">
          <button className="btn ghost only-mobile" onClick={() => setSidebarOpen((s) => !s)} title={getTranslation("toggleSidebar")}>
            {sidebarOpen ? <LuPanelRightClose /> : <LuMenu />}
          </button>
          <span className="lodrive-logo">
            <LuHome className="lodrive-logo-icon" />
            <span className="lodrive-logo-title">Lodrive</span>
          </span>
          <div className="lodrive-breadcrumbs">
            {path.map((p, i) => (
              <React.Fragment key={p.id}>
                <button
                  className={`crumb ${p.id === currentFolderId ? 'active' : ''}`}
                  onClick={() => openFolder(p.id)}
                  disabled={isLoading}
                  onDragOver={(e) => onBreadcrumbDragOver(e, p.id)}
                  onDrop={(e) => onBreadcrumbDrop(e, p.id)}
                >
                  {p.name}
                </button>
                {i < path.length - 1 && <span className="crumb-sep">/</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="lodrive-toolbar-right">
          <div className="drive-meter" title={driveSize.error ? `${getTranslation("errorPrefix")} ${driveSize.error}` : ''}>
            <div className="drive-meter-row">
              <span className="drive-meter-label">Drive</span>
              <span className="drive-meter-stats">
                {driveSize.loading ? '…' : `${formatBytes(driveSize.used)} / ${subscriptionType === "professional" ? "20 GB" : "5 GB"} ${getTranslation("used")}`}
              </span>
            </div>
            {driveSize.total > 0 && (
              <div className="drive-meter-bar" aria-hidden>
                <div
                  className={`drive-meter-fill ${driveSize.percent >= 90 ? 'warn' : driveSize.percent >= 75 ? 'mid' : ''}`}
                  style={{ width: `${Math.max(0, Math.min(100, driveSize.percent))}%` }}
                />
              </div>
            )}
          </div>

          <button className="btn ghost" onClick={() => openFolder(ROOT_ID)} title={getTranslation("goRoot")} disabled={isLoading}>
            <LuArrowLeft />
          </button>
        </div>
      </div>

      <div
        className={`lodrive-body ${sidebarOpen ? '' : 'sidebar-collapsed'} ${showUploader ? 'dock-open' : ''}`}
        onClick={() => { if (ctxMenu) closeContext(); }}
      >
        {/* Sidebar */}
        <aside className="lodrive-sidebar">
          <FolderTree
            items={items}
            currentFolderId={currentFolderId}
            renamingId={renamingId}
            renamingSource={renamingSource}
            pendingDelete={pendingDelete}
            pendingMove={pendingMove}
            onOpen={(id) => openFolder(id, { forceRefetch: false })}
            onOpenFast={quickOpen}
            hasFetched={hasFetched}
            onContext={(e, id) => onOpenContext(e, id, 'sidebar')}
            onRename={(id, name) => commitRename(id, name)}
            setRenamingId={setRenamingId}
            setRenamingSource={setRenamingSource}
            isLoading={isLoading}
            selection={selection}
            onInternalMove={moveItemsToTarget}
          />
        </aside>

        {/* Content */}
        <main
          className="lodrive-content"
          onDragEnter={onContentDragEnter}
          onDragOver={onContentDragOver}
          onDragLeave={onContentDragLeave}
          onDrop={onContentDrop}
        >
          <div className="content-header sticky-subheader">
            <h2 className="folder-title">
              <LuFolder /> {currentFolder?.name}
            </h2>
            <div className="content-actions">
              <button className="btn" onClick={createFolder} disabled={isLoading}>
                <LuFolderPlus /> {getTranslation("newFolder")}
              </button>
              <button className="btn" onClick={() => setShowUploader(true)} disabled={isLoading}>
                <LuUpload /> {getTranslation("upload")}
              </button>
            </div>
          </div>

          <div className="lodrive-grid" onClick={() => { if (ctxMenu) closeContext(); }}>
            {children.map(it => (
              <GridItem
                key={it.id}
                item={it}
                selected={selection.includes(it.id)}
                selectedIds={selection}
                renaming={renamingId === it.id && renamingSource === 'grid'}
                hoveringAsDropTarget={hoveringFolder === it.id}
                isDeleting={pendingDelete.has(it.id)}
                isMoving={pendingMove.has(it.id)}
                onOpen={() => (isFolder(it) ? openFolder(it.id, { forceRefetch: false }) : null)}
                onSelect={(multi) => {
                  setSelection(prev => {
                    if (multi) return prev.includes(it.id) ? prev.filter(x => x !== it.id) : [...prev, it.id];
                    return [it.id];
                  });
                }}
                onRename={(v) => { commitRename(it.id, v); }}
                onContext={(e) => onOpenContext(e, it.id, 'grid')}
                disableOpen={isLoading}
                onDownload={() => { items[it.id]?.type === 'file' && downloadFile(items[it.id]); }}
                onDragOverFolder={async (e) => {
                  if (!isFolder(it)) return;
                  if (e.dataTransfer) {
                    const hasInternal = !!e.dataTransfer.types?.includes(DND_INTERNAL);
                    e.dataTransfer.dropEffect = hasInternal ? 'move' : 'copy';
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  setHoveringFolder(it.id);
                }}
                onDragLeaveFolder={(e) => {
                  if (!isFolder(it)) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setHoveringFolder(prev => (prev === it.id ? null : prev));
                }}
                onDropToFolder={async (e) => {
                  if (!isFolder(it)) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setHoveringFolder(null);

                  // İç move?
                  const internal = e.dataTransfer?.getData?.(DND_INTERNAL);
                  if (internal) {
                    try {
                      const ids = JSON.parse(internal);
                      await moveItemsToTarget(it.id, ids);
                    } catch (err) {
                      console.error('internal folder drop parse error', err);
                    }
                    return;
                  }

                  // Dış dosya yükleme
                  await uploaderRef.current?.enqueueFromDataTransfer?.(e.dataTransfer, it.id);
                  setShowUploader(true);
                }}
              />
            ))}
            {children.length === 0 && !isLoading && (
              <div className="lodrive-empty">
                <div className="lodrive-empty-illustration"><LuFolder /></div>
                <div className="lodrive-empty-title">{getTranslation("emptyFolderTitle")}</div>
                <div className="lodrive-empty-sub">{getTranslation("emptyFolderSubtitle")}</div>
              </div>
            )}
          </div>
        </main>

        {/* Sağ Upload Panel */}
        <aside className={`uploader-dock ${showUploader ? 'open' : ''}`} aria-hidden={!showUploader} onClick={(e) => e.stopPropagation()}>
          <div className="uploader-dock-header">
            <div className="uploader-dock-title"><LuUpload /> {getTranslation("uploads")}</div>
            <button className="icon-btn" onClick={() => setShowUploader(false)}><LuPanelRightOpen /></button>
          </div>
          <div className="uploader-dock-body">
            <div className="lodrive-uploader-override" style={{ height: '100%' }}>
              <DragAndDropFolderAndFileUpload
                ref={uploaderRef}
                onUpload={uploadSingleFileWithHierarchy}
                onBusyChange={(busy) => setUploadsBusy(!!busy)}
                onBatchComplete={async () => {
                  await refreshVisibleTree(currentFolderId, { forceRefetch: true });
                  fetchDriveSize();
                }}
                sessionKey="lodrive-global"
                // NEW: enqueue anında o anki klasörü snapshot’la
                getDefaultTargetFolderId={() => currentFolderId}
              />
            </div>
          </div>
        </aside>

      </div>

      {/* Sağ tık menüsü */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={closeContext}
          onRename={() => {
            if (ctxMenu.id === ROOT_ID) return;
            const it = items[ctxMenu.id];
            if (!it) return; // klasör/dosya fark etmez
            setRenamingId(ctxMenu.id);
            setRenamingSource(ctxMenu.source);
            closeContext();
          }}
          onDelete={() => {
            const idsToDelete = selection.includes(ctxMenu.id) ? selection : [ctxMenu.id];
            removeMany(idsToDelete);
            closeContext();
          }}
          onMoveTo={() => {
            const idsToMove = selection.includes(ctxMenu.id) ? selection : [ctxMenu.id];
            setMovingIds(idsToMove);
            closeContext();
          }}
          // Sadece root veya busy durumlarında kapat; TÜR kısıtı kaldırıldı
          disabledRename={
            ctxMenu.id === ROOT_ID ||
            pendingDelete.has(ctxMenu.id) ||
            pendingMove.has(ctxMenu.id)
          }
          disabledDelete={
            uploadsBusy ||
            selection.includes(ROOT_ID) ||
            ctxMenu.id === ROOT_ID ||
            pendingDelete.has(ctxMenu.id) ||
            pendingMove.has(ctxMenu.id)
          }
          disabledMove={uploadsBusy || pendingDelete.has(ctxMenu.id) || pendingMove.has(ctxMenu.id)}
          canDownload={items[ctxMenu.id]?.type === 'file'}
          canDownloadDir={items[ctxMenu.id]?.type === 'folder'}
          onDownload={() => {
            const it = items[ctxMenu.id];
            if (it?.type === 'file') downloadFile(it);
            closeContext();
          }}
          onDownloadDir={() => {
            const it = items[ctxMenu.id];
            if (it?.type === 'folder') downloadDirectoryAsZip(it.id);
            closeContext();
          }}
        />
      )}

      {/* Move To Modal */}
      {movingIds && (
        <MoveToModal
          items={items}
          excludeIds={movingIds}
          onClose={() => setMovingIds(null)}
          onMove={async (targetId) => {
            await moveItemsToTarget(targetId, movingIds);
            setMovingIds(null);
          }}
        />
      )}
    </div>
  );
}

/* ----------------- Sidebar Tree (Expand/Collapse + DnD move) ----------------- */
function FolderTree({
  items, currentFolderId, onOpen, onOpenFast, hasFetched, onContext,
  renamingId, renamingSource, onRename, setRenamingId, setRenamingSource,
  isLoading, pendingDelete, pendingMove, selection, onInternalMove
}) {
  const { getTranslation } = useLanguage();

  const [editName, setEditName] = useState('');
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem('lodrive:treeExpanded');
      return raw ? JSON.parse(raw) : { [ROOT_ID]: true };
    } catch {
      return { [ROOT_ID]: true };
    }
  });

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem('lodrive:treeExpanded', JSON.stringify(expanded));
    } catch { }
  }, [expanded]);

  // Rename input değerini doldur
  useEffect(() => {
    if (renamingId && renamingSource === 'sidebar') {
      const it = items[renamingId];
      setEditName(it?.name ?? '');
    }
  }, [renamingId, renamingSource, items]);

  // parent->children map
  const byParent = useMemo(() => {
    const m = {};
    Object.values(items).forEach(it => {
      if (it.type !== 'folder') return;
      const pid = (it.parentId ?? ROOT_ID);
      if (!m[pid]) m[pid] = [];
      m[pid].push(it);
    });
    Object.keys(m).forEach(k => m[k].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    return m;
  }, [items]);

  // Mevcut klasörün patikasını otomatik açık tut
  useEffect(() => {
    const pathIds = buildPath(items, currentFolderId).map(p => p.id);
    setExpanded(prev => {
      const next = { ...prev };
      pathIds.forEach(id => { next[id] = true; });
      next[ROOT_ID] = true;
      return next;
    });
  }, [currentFolderId, items]);

  const toggle = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNavClick = (f) => {
    const isOpen = !!expanded[f.id];
    const alreadyFetched = hasFetched?.(f.id);

    if (alreadyFetched) {
      if (f.id === currentFolderId) {
        toggle(f.id);
      } else {
        onOpenFast?.(f.id);
      }
      return;
    }

    onOpen?.(f.id);
    setExpanded(prev => ({ ...prev, [f.id]: true }));
  };

  const onTreeDragOver = (e, folderId) => {
    if (!e.dataTransfer?.types?.includes(DND_INTERNAL)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onTreeDrop = async (e, folderId) => {
    const payload = e.dataTransfer?.getData?.(DND_INTERNAL);
    if (!payload) return;
    e.preventDefault();
    try {
      const ids = JSON.parse(payload);
      await onInternalMove?.(folderId, ids);
    } catch (err) {
      console.error('sidebar drop parse error', err);
    }
  };

  const renderNode = (f, depth = 0) => {
    const active = f.id === currentFolderId;
    const isEditing = renamingId === f.id && renamingSource === 'sidebar';
    const busyDeleting = pendingDelete?.has(f.id);
    const busyMoving = pendingMove?.has(f.id);
    const children = byParent[f.id] || [];
    const hasChildren = children.length > 0;
    const isOpen = !!expanded[f.id];

    return (
      <li key={f.id}>
        <div
          className={`tree-item ${active ? 'active' : ''} ${busyDeleting ? 'is-deleting' : ''} ${busyMoving ? 'is-moving' : ''}`}
          onContextMenu={(e) => !busyDeleting && !busyMoving && onContext(e, f.id)}
          title={f.name}
          aria-disabled={isLoading || busyDeleting || busyMoving}
          onDragOver={(e) => onTreeDragOver(e, f.id)}
          onDrop={(e) => onTreeDrop(e, f.id)}
        >
          {/* Caret */}
          <button
            className={`tree-caret ${hasChildren ? '' : 'invisible'}`}
            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggle(f.id); }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            disabled={!hasChildren}
          >
            {isOpen ? <LuChevronDown /> : <LuChevronRight />}
          </button>

          {/* Folder icon */}
          <button
            className="tree-nav-btn"
            onClick={() => !isLoading && !busyDeleting && !busyMoving && handleNavClick(f)}
            onDoubleClick={(e) => { e.stopPropagation(); toggle(f.id); }}
            disabled={isLoading || busyDeleting || busyMoving}
            style={{ paddingLeft: depth ? 0 : 0 }}
          >
            <LuFolder />
          </button>

          {/* Name / inline rename */}
          {isEditing ? (
            <form
              className="tree-rename-form"
              onSubmit={(e) => { e.preventDefault(); onRename(f.id, editName); }}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => onRename(f.id, editName)}
            >
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="tree-rename-input"
                maxLength={120}
                disabled={busyDeleting || busyMoving}
              />
            </form>
          ) : (
            <button
              className="tree-text-btn"
              onClick={() => !isLoading && !busyDeleting && !busyMoving && handleNavClick(f)}
              disabled={isLoading || busyDeleting || busyMoving}
            >
              <span className="tree-text">{f.name}</span>
            </button>
          )}

          {(busyDeleting || busyMoving) && <span className="tree-busy-dot" aria-hidden />}
        </div>

        {/* Children */}
        {hasChildren && isOpen && (
          <ul className="tree-children">
            {children.map(ch => renderNode(ch, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  // Root’lar (ROOT altında görünenler)
  const roots = (byParent[ROOT_ID] || []);

  return (
    <div className="folder-tree">
      <div className="tree-header-row">
        <div className="tree-header">{getTranslation("folders")}</div>
        <div className="tree-header-actions">
          <button
            className="tree-mini-btn"
            title="Expand all"
            onClick={() => {
              const all = {};
              Object.values(items).forEach(it => { if (it.type === 'folder') all[it.id] = true; });
              all[ROOT_ID] = true;
              setExpanded(all);
            }}
          >
            ⊞
          </button>
          <button
            className="tree-mini-btn"
            title="Collapse all"
            onClick={() => setExpanded({ [ROOT_ID]: true })}
          >
            ⊟
          </button>
        </div>
      </div>

      <ul className="tree-root">
        {roots.map(f => renderNode(f, 0))}
      </ul>
    </div>
  );
}

/* ----------------- Grid Item ----------------- */
function GridItem({
  item, selected, selectedIds, renaming, hoveringAsDropTarget,
  onOpen, onSelect, onRename, onContext,
  disableOpen, onDownload, onDragOverFolder, onDragLeaveFolder, onDropToFolder,
  isDeleting, isMoving
}) {
  const { getTranslation } = useLanguage();
  const [name, setName] = useState(item.name);
  useEffect(() => setName(item.name), [item.name]);

  const isFolderItem = item.type === 'folder';
  const busy = !!(isDeleting || isMoving);

  const handleDragStart = (e) => {
    try {
      const ids = selected ? selectedIds : [item.id];
      e.dataTransfer.setData(DND_INTERNAL, JSON.stringify(ids));
      e.dataTransfer.effectAllowed = 'move';
    } catch { }
  };

  return (
    <div
      className={`cell ${selected ? 'selected' : ''} ${hoveringAsDropTarget ? 'drop-hover' : ''} ${isDeleting ? 'is-deleting' : ''} ${isMoving ? 'is-moving' : ''}`}
      onClick={(e) => !busy && onSelect(e.shiftKey || e.metaKey || e.ctrlKey)}
      onContextMenu={(e) => !busy && onContext(e)}
      draggable={!busy}
      onDragStart={!busy ? handleDragStart : undefined}
      title={item.name}
      aria-disabled={disableOpen || busy}
      onDoubleClick={
        !disableOpen && !busy
          ? () => { item.type === 'folder' ? onOpen?.() : onDownload?.(); }
          : undefined
      }
      onDragOver={isFolderItem && !busy ? onDragOverFolder : undefined}
      onDragLeave={isFolderItem && !busy ? onDragLeaveFolder : undefined}
      onDrop={isFolderItem && !busy ? onDropToFolder : undefined}
    >
      <div className="cell-icon">
        {item.type === 'folder' ? <LuFolder /> : fileIconByName(item.name)}
      </div>

      {renaming ? (
        <form
          className="rename-form"
          onSubmit={(e) => { e.preventDefault(); onRename(name); }}
          onBlur={() => onRename(name)}
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rename-input"
            maxLength={120}
            disabled={busy}
          />
        </form>
      ) : (
        <div className="cell-name clamp-2">{item.name}</div>
      )}

      {item.type === 'file' && !busy && (
        <button className="cell-download" onClick={(e) => { e.stopPropagation(); onDownload?.(); }} title={getTranslation("download")}>
          ⤓
        </button>
      )}

      {!busy && (
        <button className="cell-more" onClick={onContext} aria-label={getTranslation("more")}>
          <LuMoreVertical />
        </button>
      )}

      {busy && <div className="cell-busy-overlay" aria-hidden />}
    </div>
  );
}

/* ----------------- Context Menu ----------------- */
function ContextMenu({
  x, y, onClose, onRename, onDelete, onMoveTo,
  disabledRename, disabledDelete, disabledMove, canDownload, onDownload,
  canDownloadDir, onDownloadDir
}) {
  const menuRef = useRef(null);
  const { getTranslation } = useLanguage();

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    const onClickOutside = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) onClose();
    };
    window.addEventListener('keydown', onEsc);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [onClose]);

  return (
    <div className="ctx-menu" style={{ left: x, top: y }} ref={menuRef}>
      <button className="ctx-item" onClick={onRename} disabled={disabledRename}><LuFileText /> {getTranslation("rename")}</button>
      <button className="ctx-item" onClick={onDownload} disabled={!canDownload}>⤓ {getTranslation("download")}</button>
      <button className="ctx-item" onClick={onDownloadDir} disabled={!canDownloadDir}><LuArchive /> {getTranslation("download")} (.zip)</button>
      <button className="ctx-item" onClick={onMoveTo} disabled={disabledMove}><LuMove /> {getTranslation("moveTo")}</button>
      <button className="ctx-item danger" onClick={onDelete} disabled={disabledDelete}><LuX /> {getTranslation("delete")}</button>
    </div>
  );
}

/* ----------------- Move To Modal ----------------- */
function MoveToModal({ items, excludeIds, onClose, onMove }) {
  const [target, setTarget] = useState(ROOT_ID);
  const { getTranslation } = useLanguage();

  const forbidden = useMemo(() => {
    const set = new Set(excludeIds || []);
    (excludeIds || []).forEach(id => {
      const it = items[id];
      if (it && it.type === 'folder') {
        getDescendants(items, id).forEach(d => set.add(d));
      }
    });
    return set;
  }, [items, excludeIds]);

  const folders = useMemo(() =>
    Object.values(items)
      .filter(it => it.type === 'folder')
      .sort((a, b) => a.name.localeCompare(b.name, 'tr')), [items]);

  return (
    <div className="lodrive-modal-overlay" onClick={onClose}>
      <div className="lodrive-modal" onClick={e => e.stopPropagation()}>
        <div className="lodrive-modal-header">
          <div className="lodrive-modal-title"><LuMove /> {getTranslation("moveToTitle")}</div>
          <button className="icon-btn" onClick={onClose}><LuX /></button>
        </div>
        <div className="lodrive-modal-body">
          <div className="move-list">
            <label className="move-row">
              <input
                type="radio"
                name="target"
                checked={target === ROOT_ID}
                onChange={() => setTarget(ROOT_ID)}
              />
              <span className="move-name"><LuFolder /> {getTranslation("rootFolder")} (Lodrive)</span>
            </label>

            {folders.map(f => {
              const disabled = forbidden.has(f.id);
              return (
                <label key={f.id} className={`move-row ${disabled ? 'disabled' : ''}`}>
                  <input
                    type="radio"
                    name="target"
                    disabled={disabled}
                    checked={target === f.id}
                    onChange={() => setTarget(f.id)}
                  />
                  <span className="move-name"><LuFolder /> {f.name}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="lodrive-modal-footer">
          <button className="btn ghost" onClick={onClose}><LuX /> {getTranslation("cancel")}</button>
          <button className="btn" onClick={() => onMove(target)}><LuCheck /> {getTranslation("move")}</button>
        </div>
      </div>
    </div>
  );
}